/**
 * Application Constants
 * Extracted from App.js as part of refactoring.
 * 
 * Centralized constants for consistent usage across the application.
 */

// =============================================================================
// APPLICATION CONFIG
// =============================================================================

/**
 * Application version string
 * Update this when deploying new versions
 */
export const APP_VERSION = "2.1.0-pagination";

// =============================================================================
// CLIENT STATUSES
// =============================================================================

/**
 * Client status options for dropdown selectors
 * Used in client management, filtering, and forms
 */
export const CLIENT_STATUSES = [
  { value: 'pending_intake', label: 'Pending Waitlist Approval' },
  { value: 'corrections_requested', label: 'Corrections Requested' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'discharged', label: 'Discharged' },
  { value: 'archived', label: 'Archived' }
];

/**
 * Get status label from value
 * @param {string} statusValue - The status value to look up
 * @returns {string} The human-readable label
 */
export const getStatusLabel = (statusValue) => {
  const status = CLIENT_STATUSES.find(s => s.value === statusValue);
  return status?.label || statusValue;
};

/**
 * Check if a status is considered "active" (client is receiving services)
 * @param {string} statusValue - The status value to check
 * @returns {boolean}
 */
export const isActiveStatus = (statusValue) => {
  return ['active', 'pending_intake', 'waitlist', 'corrections_requested'].includes(statusValue);
};

// =============================================================================
// CLINICAL OPTIONS
// =============================================================================

/**
 * Symptom options for intake/assessment forms
 */
export const SYMPTOM_OPTIONS = [
  "Depression/Sadness", "Anxiety/Worry", "Panic Attacks", "Mood Swings",
  "Anger/Irritability", "Sleep Problems", "Eating/Appetite Changes",
  "Concentration Difficulties", "Fatigue/Low Energy", "Social Withdrawal",
  "Hallucinations", "Delusions", "Suicidal Thoughts", "Self-Harm",
  "Substance Use", "Trauma/PTSD Symptoms", "Obsessive Thoughts",
  "Compulsive Behaviors", "Hyperactivity", "Impulsivity", "Memory Problems", "Other"
];

/**
 * Symptoms that indicate urgent need - auto-suggest urgent flag
 */
export const URGENT_SYMPTOMS = ["Suicidal Thoughts", "Self-Harm", "Hallucinations", "Delusions"];

/**
 * Check if any symptoms are urgent
 * @param {string[]} symptoms - Array of symptom strings
 * @returns {boolean}
 */
export const hasUrgentSymptoms = (symptoms) => {
  return symptoms?.some(s => URGENT_SYMPTOMS.includes(s)) || false;
};

// =============================================================================
// LANGUAGE OPTIONS
// =============================================================================

/**
 * Language options for client preferences and filtering
 */
export const LANGUAGE_OPTIONS = [
  "English", "Spanish", "Portuguese", "French", "Mandarin", 
  "Cantonese", "Vietnamese", "Korean", "Arabic", "Russian", 
  "Haitian Creole", "Other"
];

// =============================================================================
// FORM FIELD TYPES
// =============================================================================

/**
 * Field types that don't accept user input (decorative/structural)
 */
export const NON_INPUT_FIELD_TYPES = [
  'section_header', 'heading', 'divider', 'spacer', 'static_text', 'row_placeholder'
];

/**
 * Check if a field type accepts user input
 * @param {string} fieldType - The field type to check
 * @returns {boolean}
 */
export const isInputFieldType = (fieldType) => {
  return !NON_INPUT_FIELD_TYPES.includes(fieldType);
};

// =============================================================================
// NOTE STATUSES
// =============================================================================

/**
 * Note status options
 */
export const NOTE_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'locked', label: 'Locked' }
];

// =============================================================================
// DEFAULT TABS BY ROLE
// =============================================================================

/**
 * All available dashboard tabs
 */
export const ALL_DASHBOARD_TABS = [
  'appointments', 'clients', 'groups', 'notes', 'billing', 
  'waitlist', 'admin', 'cpt', 'eligibility', 'authorizations',
  'medication-management', 'messaging'
];

/**
 * Default visible tabs for provider role (no role_id assigned)
 */
export const PROVIDER_DEFAULT_TABS = ['appointments', 'clients'];

/**
 * Default visible tabs for office/reception role
 */
export const OFFICE_DEFAULT_TABS = ['appointments', 'clients', 'eligibility'];

/**
 * Default visible tabs for unknown roles (restrictive)
 */
export const DEFAULT_TABS = ['appointments', 'clients'];


/**
 * Default permissions by role — used as fallback when roles haven't loaded from the API yet.
 * Keep in sync with the roles collection in the database.
 */
export const OFFICE_DEFAULT_PERMISSIONS = [
  'view_clients', 'create_clients', 'edit_clients', 'delete_clients', 'archive_clients',
  'view_appointments', 'create_appointments', 'edit_appointments', 'delete_appointments',
  'view_notes', 'create_notes', 'edit_notes', 'delete_notes',
  'view_forms', 'view_all_clients',
  'view_diagnoses', 'add_diagnoses', 'edit_diagnoses', 'archive_diagnoses',
  'view_medications', 'add_medications', 'edit_medications', 'archive_medications',
  'view_cpt_codes', 'view_authorizations', 'create_authorizations', 'edit_authorizations', 'deduct_units',
  'view_organizations', 'view_insurances', 'view_locations', 'view_groups',
  'view_reports', 'export_data', 'view_users',
  'edit_client_demographics', 'edit_client_clinical', 'edit_client_insurance',
  'manage_service_enrollments', 'view_service_enrollments'
];

export const PROVIDER_DEFAULT_PERMISSIONS = [
  'view_clients', 'view_appointments', 'create_appointments', 'edit_appointments',
  'view_notes', 'create_notes', 'edit_notes', 'sign_notes', 'delete_notes', 'delete_appointments',
  'view_diagnoses', 'add_diagnoses', 'edit_diagnoses', 'archive_diagnoses',
  'view_medications', 'add_medications', 'archive_medications',
  'view_authorizations', 'create_authorizations', 'edit_authorizations', 'deduct_units',
  'view_cpt_codes', 'view_forms', 'view_groups', 'view_users',
  'view_locations', 'view_insurances', 'view_organizations',
  'edit_client_clinical', 'edit_client_status', 'view_service_enrollments'
];

export const SUPERVISOR_DEFAULT_PERMISSIONS = [
  ...PROVIDER_DEFAULT_PERMISSIONS,
  'manage_forms', 'delete_appointments', 'delete_notes'
];

export const RECEPTION_DEFAULT_PERMISSIONS = [
  'view_clients', 'create_clients', 'edit_clients',
  'view_appointments', 'create_appointments', 'edit_appointments',
  'view_forms', 'edit_client_status', 'manage_provider_assignments', 'view_archived_clients'
];

export const INTERN_DEFAULT_PERMISSIONS = [
  'view_clients', 'view_users', 'view_appointments', 'create_appointments', 'edit_appointments', 'delete_appointments',
  'view_notes', 'create_notes', 'edit_notes', 'sign_notes', 'delete_notes',
  'view_authorizations', 'deduct_units', 'view_cpt_codes',
  'view_medications', 'add_medications', 'view_diagnoses', 'add_diagnoses', 'edit_diagnoses', 'archive_diagnoses',
  'view_forms', 'view_organizations', 'view_locations', 'view_insurances', 'view_groups', 'view_service_enrollments'
];
